// Variable-rate resampling: read a file at a moving position. It covers two
// jobs: Re-Pitch through a warp map (speed follows each segment's slope, so
// pitch does too, as Live's Re-Pitch mode does), and the second half of a
// pitch shift (vocode to a longer length, then play it back faster). A
// windowed sinc whose cutoff follows the local rate, so speeding up does
// not alias. The kernel is a lookup table, not per-tap trig.

import type { PcmSource } from "@web-shared/lib/wav";
import type { StretchedPcm } from "@web-shared/lib/warp";

/** Zero crossings of the kernel either side of the centre. */
const HALF_TAPS = 12;
/** Table points per zero crossing. */
const TABLE_RES = 256;

let table: Float64Array | null = null;

/** sinc(x)·Blackman(x / HALF_TAPS) for x in [0, HALF_TAPS], sampled finely. */
function kernel(): Float64Array {
  if (table) return table;
  const n = HALF_TAPS * TABLE_RES + 2;
  const t = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const x = i / TABLE_RES;
    const sinc = x === 0 ? 1 : Math.sin(Math.PI * x) / (Math.PI * x);
    const w = x >= HALF_TAPS ? 0 : 0.42 + 0.5 * Math.cos((Math.PI * x) / HALF_TAPS) + 0.08 * Math.cos((2 * Math.PI * x) / HALF_TAPS);
    t[i] = sinc * w;
  }
  table = t;
  return t;
}

function tap(t: Float64Array, x: number): number {
  const a = Math.abs(x) * TABLE_RES;
  const i = Math.floor(a);
  if (i >= t.length - 1) return 0;
  const f = a - i;
  return t[i]! * (1 - f) + t[i + 1]! * f;
}

/**
 * `outLen` samples, output sample o read from input position `mapIn(o)`
 * (fractional). The map must not run backwards. Positions outside the
 * input read silence.
 */
export function resampleBy(source: PcmSource, outLen: number, mapIn: (outSample: number) => number): StretchedPcm {
  const t = kernel();
  const channels = Math.max(1, source.numberOfChannels);
  const inputs = Array.from({ length: channels }, (_, c) => source.getChannelData(Math.min(c, source.numberOfChannels - 1)));
  const inLen = source.length;
  const outs = inputs.map(() => new Float32Array(new ArrayBuffer(Math.max(0, outLen) * 4)));
  const at = new Float64Array(Math.max(0, outLen) + 1);
  for (let o = 0; o <= outLen; o++) at[o] = mapIn(o);
  for (let o = 0; o < outLen; o++) {
    const pos = at[o]!;
    // Local rate: how many input samples one output sample spans.
    const rate = o > 0 ? (at[o + 1]! - at[o - 1]!) / 2 : at[1]! - pos;
    const cutoff = rate > 1 ? 1 / rate : 1;
    const reach = HALF_TAPS / cutoff;
    const lo = Math.max(0, Math.ceil(pos - reach));
    const hi = Math.min(inLen - 1, Math.floor(pos + reach));
    if (hi < lo) continue;
    for (let c = 0; c < channels; c++) {
      const src = inputs[c]!;
      let sum = 0;
      for (let j = lo; j <= hi; j++) sum += src[j]! * tap(t, (pos - j) * cutoff);
      outs[c]![o] = sum * cutoff;
    }
  }
  return {
    sampleRate: source.sampleRate,
    numberOfChannels: outs.length,
    length: Math.max(0, outLen),
    channels: outs,
    getChannelData: (c) => outs[c] ?? outs[0]!,
  };
}
